import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import {
  X, Loader2, Save, Bell, Copy, AlertTriangle, Upload, StickyNote,
  Car, CreditCard, Activity, Pencil, HeartPulse, Users,
} from "lucide-react";
import { COUNTRIES } from "../../utils/countries";

const API = "/api";
const GOLD = "#D4A017";

const fmt = (iso, f = "d MMM yyyy HH:mm") => {
  if (!iso) return "—";
  try { return format(new Date(iso), f, { locale: es }); } catch (_) { return "—"; }
};

const SUB_LABEL = {
  pending_payment: "Pago pendiente", active: "Activa", standby: "Standby",
  expired: "Vencida", disabled: "Inhabilitada",
};

const Section = ({ icon: Icon, title, children, testId }) => (
  <div className="bg-[#0B2545]/70 rounded-xl border border-white/10 p-4 mb-4" data-testid={testId}>
    <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#5BC0FF]" /> {title}
    </h3>
    {children}
  </div>
);

export default function UserDrawer({ userId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [profile, setProfile] = useState({});
  const [editingProfile, setEditingProfile] = useState(false);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [subModal, setSubModal] = useState(null); // {action, needsReason, needsAmount, unit}
  const [subModalValue, setSubModalValue] = useState("");
  const [subModalReason, setSubModalReason] = useState("");
  const [regenVehicle, setRegenVehicle] = useState(null);
  const [regenConfirmText, setRegenConfirmText] = useState("");
  const [editVehicle, setEditVehicle] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await axios.get(`${API}/admin/uc/users/${userId}`);
      setData(r.data);
      setProfile(r.data.profile);
    } catch (e) {
      const notFound = e.response?.status === 404;
      setLoadError(notFound
        ? "Este cliente ya no existe. Puede ser un registro de prueba que se borró al terminar una prueba."
        : (e.response?.data?.detail || "No se pudo cargar la ficha del cliente."));
      if (!notFound) toast.error("Error al cargar la ficha");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/uc/users/${userId}/profile`, {
        full_name: profile.full_name, email: profile.email,
        phone: profile.phone, country_code: profile.country_code,
      });
      toast.success(Object.keys(r.data.changed || {}).length ? "Perfil actualizado (auditado)" : "Sin cambios");
      setEditingProfile(false);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setBusy(false); }
  };

  const subAction = async (action, extra = {}) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/subscriptions/${userId}/action`, { action, ...extra });
      toast.success(`Acción "${action}" aplicada (auditada)`);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la acción");
    } finally { setBusy(false); setSubModal(null); setSubModalValue(""); setSubModalReason(""); }
  };

  const confirmSubModal = () => {
    const n = parseInt(subModalValue, 10);
    if (subModal.needsAmount && (!n || n < 1)) { toast.error("Cantidad inválida"); return; }
    if (subModal.needsReason && !subModalReason.trim()) { toast.error("El motivo es obligatorio"); return; }
    const extra = { note: subModalReason || undefined };
    if (subModal.action === "extend_days") extra.days = n;
    if (subModal.action === "gift" || subModal.action === "extend") extra.months = n;
    subAction(subModal.action, extra);
  };

  const [pushGuideHint, setPushGuideHint] = useState(false);

  // iter69 F1 — read-only forensics download (works in PROD from this ficha)
  const downloadForensics = async () => {
    try {
      const r = await axios.get(`${API}/admin/uc/users/${userId}/push-forensics`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `push_forensics_${userId.slice(0, 8)}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Forense push descargado (72h, solo lectura)");
    } catch (_e) { toast.error("Error al generar forense"); }
  };

  const testPush = async () => {
    // iter68 F4 — no red error when the user simply has 0 enabled devices:
    // show the guide hint instead (with inline "Reenviar guía").
    if ((data?.operational?.push_devices_enabled ?? 0) === 0) {
      setPushGuideHint(true);
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/uc/users/${userId}/test-push`);
      if (r.data.ok) {
        toast.success(`Aviso aceptado por el proveedor para ${r.data.sent} dispositivo(s). Aceptado no es entregado: confírmalo con el cliente.`);
      } else if ((r.data.sent || 0) === 0 && (r.data.failed || 0) === 0) {
        setPushGuideHint(true);
      } else {
        toast.error(`Fallo real de entrega (enviados: ${r.data.sent || 0}, fallidos: ${r.data.failed || 0}${r.data.http_status ? `, http ${r.data.http_status}` : ""})`);
      }
    } catch (e) {
      toast.error(`${e.response?.data?.detail || "Error al enviar push"}${e.response?.status ? ` (http ${e.response.status})` : ""}`);
    } finally { setBusy(false); }
  };

  // iter69-B A1 — 🫀 Revivir avisos desde la ficha (cascada sin teléfono)
  const reviveUser = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/uc/users/${userId}/revive`);
      if (r.data.rate_limited) {
        toast.info("Revival ya enviado en las últimas 24h — el banner rojo sigue activo en su dashboard");
      } else if ((r.data.push?.sent || 0) > 0) {
        toast.success(`🫀 Push de revival enviado (${r.data.push.sent} dispositivo/s) + banner rojo activado`);
      } else {
        toast.warning("Push no entregado (dispositivo muerto), pero el banner rojo quedó activo en su dashboard");
      }
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al revivir avisos");
    } finally { setBusy(false); }
  };

  // iter67 F2.3 — resend install guide (copy link / wa.me) + [ADMIN_GUIDE_SENT] audit
  const guideUrl = `${window.location.origin}/ayuda/usar-en-navegador`;
  const sendGuide = async (via) => {
    try {
      if (via === "wa") {
        const phone = (data?.profile?.phone || "").replace(/[^0-9]/g, "");
        const msg = encodeURIComponent(
          `Hola 👋 Soy soporte de AvisoCar. Para recibir tus avisos instala AvisoCar en tu teléfono siguiendo esta guía (toma 10 segundos): ${guideUrl}`
        );
        window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
      } else {
        await navigator.clipboard.writeText(guideUrl);
        toast.success("Link de la guía copiado");
      }
      await axios.post(`${API}/admin/uc/users/${userId}/guide-sent`);
    } catch (_e) {
      toast.error("No se pudo copiar/registrar");
    }
  };

  const saveVehicle = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/uc/users/${userId}/vehicles/${editVehicle.id}`, {
        name: editVehicle.name, plate_number: editVehicle.plate_number,
        primary_contact: editVehicle.primary_contact, qr_status: editVehicle.qr_status,
      });
      toast.success("Vehículo actualizado (auditado)");
      setEditVehicle(null);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar vehículo");
    } finally { setBusy(false); }
  };

  const regenerateQR = async () => {
    if (regenConfirmText !== "REGENERAR") { toast.error('Escribe "REGENERAR" para confirmar'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/uc/users/${userId}/vehicles/${regenVehicle.id}/regenerate-qr`, {
        confirm: "REGENERAR", reason: "admin manual",
      });
      toast.success("QR regenerado — el sticker impreso anterior YA NO FUNCIONA");
      setRegenVehicle(null); setRegenConfirmText("");
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al regenerar");
    } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/admin/uc/users/${userId}/notes`, { text: noteText });
      setNoteText("");
      toast.success("Nota guardada");
      await load();
    } catch (e) { toast.error("Error al guardar nota"); }
  };

  const uploadVoucher = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("voucher", file);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/uc/users/${userId}/vouchers`, fd);
      toast.success(r.data.attached_to_pending
        ? "Voucher adjuntado al pago pendiente"
        : "Voucher subido — se creó un pago pendiente");
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir voucher");
    } finally { setBusy(false); }
  };

  const reviewPayment = async (paymentId, status) => {
    const notes = status === "rejected" ? window.prompt("Motivo del rechazo:") : undefined;
    if (status === "rejected" && !notes) return;
    setBusy(true);
    try {
      await axios.put(`${API}/admin/payments/${paymentId}/review`, { status, admin_notes: notes });
      toast.success(status === "approved" ? "Pago aprobado" : "Pago rechazado");
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al revisar pago");
    } finally { setBusy(false); }
  };

  const sub = data?.subscription;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="user-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-[#071B2F] border-l border-white/10 overflow-y-auto p-5">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white" data-testid="user-drawer-close">
          <X className="w-5 h-5" />
        </button>

        {loading ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando ficha...</div>
        ) : loadError ? (
          <div className="p-10 text-center" data-testid="user-drawer-error">
            <p className="font-medium text-slate-200">No se pudo abrir la ficha</p>
            <p className="mt-2 text-sm text-slate-400">{loadError}</p>
            <Button onClick={onClose} className="mt-5 bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="user-drawer-error-close">
              Cerrar
            </Button>
          </div>
        ) : !data ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando ficha...</div>
        ) : (
          <>
            <div className="mb-4 pr-8">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>{data.profile.full_name}</h2>
              <p className="text-slate-400 text-sm">{data.profile.email} · {data.profile.country_code} · registrado {fmt(data.profile.created_at, "d MMM yyyy")}</p>
            </div>

            {/* iter67 F2.2 — banner semáforo con motivo y acción sugerida */}
            {data.operational && (() => {
              const op = data.operational;
              const cfg = {
                green: { cls: "bg-[#1B84FF]/10 border-emerald-400/40 text-emerald-300", icon: "🟢" },
                yellow: { cls: "bg-amber-500/10 border-amber-400/40 text-amber-300", icon: "🟡" },
                red: { cls: "bg-red-500/10 border-red-400/40 text-red-300", icon: "🔴" },
              }[op.status] || { cls: "bg-slate-500/10 border-slate-500/40 text-slate-300", icon: "⚪" };
              const HINTS = {
                operativo: "Todo listo: recibe avisos y llamadas de su QR.",
                sin_push: "Falta: dispositivo push → Reenviar guía de instalación.",
                sin_vehiculo_activo: "Falta: vehículo con QR activo → revisa la sección Vehículos.",
                pago_pendiente: "Falta: confirmar pago → revisa Pagos abajo.",
                vencida: "Membresía vencida → Aprobar / Extender en Suscripción.",
                inhabilitada: "Cuenta inhabilitada por admin → Habilitar en Suscripción.",
                standby: "En standby → Habilitar en Suscripción.",
                rechazada: "Pago rechazado → revisa Pagos abajo.",
              };
              return (
                <div className={`mb-4 p-3 rounded-xl border ${cfg.cls}`} data-testid="drawer-operational-banner">
                  <p className="text-sm font-bold">{cfg.icon} {op.label} — {HINTS[op.reason] || op.reason}</p>
                  <p className="text-[11px] opacity-80 mt-0.5">
                    Push: {op.push_devices_enabled} dispositivo(s) habilitado(s) · {op.vehicles_active}/{op.vehicles_total} vehículos con QR activo
                  </p>
                  {op.reason === "sin_push" && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Button size="sm" onClick={() => sendGuide("copy")}
                        className="bg-amber-500 hover:bg-amber-400 text-black h-7 text-xs font-bold" data-testid="drawer-send-guide-copy">
                        <Copy className="w-3 h-3 mr-1" /> Copiar link de la guía
                      </Button>
                      {data.profile.phone && (
                        <Button size="sm" onClick={() => sendGuide("wa")}
                          className="bg-[#1B84FF] hover:bg-[#1B84FF] h-7 text-xs" data-testid="drawer-send-guide-wa">
                          Reenviar guía por WhatsApp
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* a) Perfil */}
            <Section icon={Pencil} title="Perfil" testId="drawer-profile-section">
              {editingProfile ? (
                <div className="space-y-2">
                  <Input value={profile.full_name || ""} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                    placeholder="Nombre" className="bg-[#0B2545] border-white/15 text-white" data-testid="drawer-profile-name" />
                  <Input value={profile.email || ""} onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    placeholder="Email" className="bg-[#0B2545] border-white/15 text-white" data-testid="drawer-profile-email" />
                  <Input value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="Teléfono (E.164, ej: +51987654321)" className="bg-[#0B2545] border-white/15 text-white" data-testid="drawer-profile-phone" />
                  <select value={profile.country_code || "PE"} onChange={(e) => setProfile({ ...profile, country_code: e.target.value })}
                    className="w-full h-10 px-3 rounded-md bg-[#0B2545] border border-white/15 text-slate-300 text-sm" data-testid="drawer-profile-country">
                    {COUNTRIES.map((c) => <option key={c.iso} value={c.iso}>{c.flag} {c.name}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveProfile} disabled={busy} className="bg-[#1B84FF] hover:bg-[#1B84FF]" data-testid="drawer-profile-save">
                      <Save className="w-3.5 h-3.5 mr-1" /> Guardar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingProfile(false); setProfile(data.profile); }} className="text-slate-400">Cancelar</Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-300 space-y-1">
                  <p>Teléfono: <span className="text-white">{data.profile.phone || "—"}</span></p>
                  <p>Último acceso: <span className="text-white">{fmt(data.profile.last_login_at)}</span></p>
                  <Button size="sm" variant="outline" onClick={() => setEditingProfile(true)}
                    className="mt-2 border-white/15 text-slate-300" data-testid="drawer-profile-edit-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar perfil
                  </Button>
                </div>
              )}
            </Section>

            {/* d) Suscripción — MASTER ADMIN V2 §2: acciones CONTEXTUALES */}
            <Section icon={CreditCard} title="Membresía y cobro" testId="drawer-subscription-section">
              <div className="text-sm text-slate-300 mb-3">
                Estado: <span className="text-white font-medium">{SUB_LABEL[sub.status] || sub.status}</span>
                {" · "}Expira: <span className="text-white">{fmt(sub.expires_at, "d MMM yyyy")}</span> ({sub.days_remaining} días)
                {" · "}Método: <span className="text-white">{sub.method || "—"}</span>
              </div>

              {data.membership_grant?.grant_type === "complimentary" && (
                <div className="mb-3 rounded-lg border border-amber-400/25 bg-amber-500/10 p-3 text-xs text-amber-100"
                     data-testid="drawer-complimentary-notice">
                  <p className="font-semibold">Membresía de cortesía (sin cobro).</p>
                  <p className="mt-1">
                    {data.membership_grant.granted_months} mes(es) otorgados el{" "}
                    {fmt(data.membership_grant.granted_at, "d MMM yyyy")}.
                    Motivo: {data.membership_grant.grant_reason || "sin motivo registrado"}.
                  </p>
                  <p className="mt-1 opacity-80">No existe ningún pago asociado y no cuenta como ingreso.</p>
                </div>
              )}

              {/* Comprobante real antes de decidir */}
              {(() => {
                const ctx = data.payment_approval_context || {};
                const tone = ctx.voucher_state === "voucher_uploaded"
                  ? "border-emerald-400/25 bg-emerald-500/10 text-emerald-100"
                  : ctx.voucher_state === "awaiting_voucher"
                    ? "border-amber-400/25 bg-amber-500/10 text-amber-100"
                    : "border-white/10 bg-black/30 text-slate-300";
                return (
                  <div className={`mb-3 rounded-lg border p-3 text-xs ${tone}`} data-testid="drawer-payment-context">
                    <p className="font-semibold">
                      {ctx.voucher_state === "voucher_uploaded" ? "Pago pendiente con comprobante"
                        : ctx.voucher_state === "awaiting_voucher" ? "Solicitud sin comprobante"
                          : "Sin solicitud de pago"}
                    </p>
                    <p className="mt-1">{ctx.voucher_note}</p>
                    {ctx.has_pending && (
                      <p className="mt-1 opacity-90">
                        {ctx.plan_months ? `${ctx.plan_months} meses · ` : ""}
                        {ctx.amount != null ? `${ctx.currency} ${ctx.amount}` : "importe sin definir"}
                        {ctx.method ? ` · ${ctx.method}` : ""}
                        {ctx.payment_ref ? ` · ref. ${ctx.payment_ref}` : ""}
                      </p>
                    )}
                    {ctx.has_voucher && (ctx.payment_id || ctx.voucher_url) && (
                      <a
                        href={ctx.voucher_url || `/admin/payments?focus=${ctx.payment_id}`}
                        target={ctx.voucher_url ? "_blank" : undefined}
                        rel={ctx.voucher_url ? "noreferrer" : undefined}
                        className="mt-2 inline-block rounded-md bg-white/10 px-2.5 py-1 font-semibold hover:bg-white/20"
                        data-testid="drawer-open-voucher"
                      >
                        {ctx.record_kind === "renewal_request"
                          ? "Abrir el comprobante de renovación"
                          : "Abrir el comprobante en la cola de pagos"}
                      </a>
                    )}
                  </div>
                );
              })()}

              {(() => {
                const actions = data.available_actions || [];
                const GROUPS = [
                  ["payment_approval", "Aprobación de pago"],
                  ["complimentary", "Membresía de cortesía"],
                  ["extension", "Extensión"],
                  ["suspension", "Suspensión"],
                  ["reactivation", "Reactivación"],
                ];
                const meta = {
                  approve: { cls: "bg-[#1B84FF]/80 hover:bg-[#1B84FF]" },
                  reject: { cls: "bg-red-700/70 hover:bg-red-600" },
                  gift: { cls: "text-black hover:opacity-90", style: { backgroundColor: GOLD },
                          modal: { action: "gift", title: "Dar membresía de cortesía", needsAmount: true, needsReason: true, unit: "meses" } },
                  extend_days: { cls: "text-black hover:opacity-90", style: { backgroundColor: GOLD },
                                 modal: { action: "extend_days", title: "Extender X días", needsAmount: true, needsReason: true, unit: "días" } },
                  standby: { cls: "bg-amber-600/70 hover:bg-amber-500" },
                  disable: { cls: "bg-red-700/70 hover:bg-red-600" },
                  enable: { cls: "bg-slate-700 hover:bg-slate-600" },
                };
                return GROUPS.map(([group, groupLabel]) => {
                  const items = actions.filter((a) => a.group === group);
                  if (items.length === 0) return null;
                  return (
                    <div key={group} className="mb-3">
                      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        {groupLabel}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((a) => {
                          const m = meta[a.key] || {};
                          return (
                            <Button
                              key={a.key}
                              size="sm"
                              disabled={busy || !a.enabled}
                              title={a.enabled ? a.label : a.reason_disabled}
                              onClick={() => (m.modal ? setSubModal(m.modal) : subAction(a.key))}
                              className={`h-7 text-xs ${m.cls || "bg-slate-700 hover:bg-slate-600"} disabled:cursor-not-allowed disabled:opacity-35`}
                              style={m.style}
                              data-testid={`drawer-sub-${a.key}`}
                            >
                              {a.label}
                            </Button>
                          );
                        })}
                      </div>
                      {items.filter((a) => !a.enabled).map((a) => (
                        <p key={a.key} className="mt-1 text-[10px] text-slate-500"
                           data-testid={`drawer-action-why-${a.key}`}>
                          {a.label}: {a.reason_disabled}
                        </p>
                      ))}
                    </div>
                  );
                });
              })()}
            </Section>

            {/* RC-WEB-5 GATE 24 — canonical trusted-contact + routing state, the
                SAME contract the standalone admin page renders. */}
            <Section icon={Users} title="Contactos de confianza y enrutamiento canónico" testId="admin-canonical-routing-card">
              {(data.trusted_contacts || []).length === 0 ? (
                <p className="text-sm text-slate-300" data-testid="admin-trusted-empty">
                  Sin contactos de confianza. Los avisos llegan solo al dueño.
                </p>
              ) : (
                <div className="space-y-2 mb-3">
                  {(data.trusted_contacts || []).map((tc) => (
                    <div key={tc.public_ref}
                         className="rounded-lg bg-black/30 border border-white/10 p-3 text-sm text-slate-200 flex flex-wrap items-center gap-x-4 gap-y-1"
                         data-testid={`admin-trusted-row-${tc.public_ref}`}>
                      <span className="font-semibold text-white">{tc.display_name || tc.public_ref}</span>
                      <span className="rounded-full bg-[#1B84FF]/20 px-2 py-0.5 text-xs text-[#5BC0FF]">{tc.status}</span>
                      <span>Mensajes: {tc.permissions?.receive_messages ? "sí" : "no"}</span>
                      <span>Llamadas: {tc.permissions?.receive_calls ? "sí" : "no"}</span>
                      <span className="text-slate-400">Perfiles: {(tc.vehicle_ids || []).length}</span>
                    </div>
                  ))}
                </div>
              )}
              {(data.vehicles || []).map((v) => (
                <div key={`routing-${v.id}`} className="rounded-lg bg-black/30 border border-white/10 p-3 mb-2 text-xs text-slate-300"
                     data-testid={`admin-routing-${v.id}`}>
                  <p className="text-white text-sm font-semibold">{v.name}</p>
                  <p>QR v{v.qr_version ?? 1} · {v.qr_status} {v.notices_paused ? "· avisos en pausa" : ""}</p>
                  <p>Mensajes → {v.routing?.message_recipient_count ?? 0} destinatario(s): {(v.routing?.message_recipients || []).join(", ") || "—"}</p>
                  <p>Llamadas → {v.routing?.call_recipient_count ?? 0} destinatario(s): {(v.routing?.call_recipients || []).join(", ") || "—"}</p>
                  {(v.legacy_contact_data?.primary_contact || v.legacy_contact_data?.emergency_contact_1) && (
                    <p className="mt-1 text-amber-300" data-testid={`admin-legacy-contact-${v.id}`}>
                      Dato de contacto heredado (legacy, no se usa para enrutar): {v.legacy_contact_data.primary_contact || v.legacy_contact_data.emergency_contact_1}
                    </p>
                  )}
                </div>
              ))}
              <p className="text-xs text-slate-300" data-testid="admin-drawer-account-phone">
                Teléfono de cuenta (solo cuenta/soporte, nunca visible al escáner): {data.profile?.phone || "—"}
              </p>
              {data.notification_settings && (
                <div className="mt-2 rounded-lg bg-black/30 border border-white/10 p-3 text-xs text-slate-300" data-testid="admin-notification-settings">
                  Preferencias del usuario (solo lectura): mensajes {data.notification_settings.messages_enabled ? "sí" : "no"} ·
                  llamadas {data.notification_settings.calls_enabled ? "sí" : "no"} ·
                  avisos urgentes {data.notification_settings.urgent_reminders_enabled ? "sí" : "no"} ·
                  navegador {data.notification_settings.browser_push_subscriptions ?? 0} ·
                  app {data.notification_settings.app_devices_registered ?? 0}
                </div>
              )}
            </Section>

            {/* b) Vehículos */}
            <Section icon={Car} title={`Vehículos (${data.vehicles.length})`} testId="drawer-vehicles-section">
              {data.vehicles.map((v) => (
                <div key={v.id} className="rounded-lg bg-black/30 border border-white/10 p-3 mb-2 text-sm" data-testid={`drawer-vehicle-${v.id}`}>
                  {editVehicle?.id === v.id ? (
                    <div className="space-y-2">
                      <Input value={editVehicle.name || ""} onChange={(e) => setEditVehicle({ ...editVehicle, name: e.target.value })}
                        placeholder="Nombre" className="bg-[#0B2545] border-white/15 text-white h-9" />
                      <Input value={editVehicle.plate_number || ""} onChange={(e) => setEditVehicle({ ...editVehicle, plate_number: e.target.value })}
                        placeholder="Placa" className="bg-[#0B2545] border-white/15 text-white h-9" />
                      <Input value={editVehicle.primary_contact || ""} onChange={(e) => setEditVehicle({ ...editVehicle, primary_contact: e.target.value })}
                        placeholder="Contacto heredado (legacy, no se usa para enrutar)" className="bg-[#0B2545] border-white/15 text-white h-9" />
                      <select value={editVehicle.qr_status || "active"} onChange={(e) => setEditVehicle({ ...editVehicle, qr_status: e.target.value })}
                        className="w-full h-9 px-2 rounded-md bg-[#0B2545] border border-white/15 text-slate-300 text-sm">
                        <option value="active">QR activo</option>
                        <option value="inactive">QR desactivado</option>
                      </select>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveVehicle} disabled={busy} className="bg-[#1B84FF] h-7 text-xs" data-testid="drawer-vehicle-save">Guardar</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditVehicle(null)} className="text-slate-400 h-7 text-xs">Cancelar</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-medium">{v.name || "Vehículo"} {v.plate_number && <span className="text-slate-400">· {v.plate_number}</span>}</p>
                          <p className="text-slate-500 text-xs">QR {v.qr_status} · pago {v.payment_status} · rate-limit {v.rate_limit.used}/{v.rate_limit.limit} (5 min)</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setEditVehicle(v)} className="border-white/15 text-slate-300 h-7 text-xs" data-testid={`drawer-vehicle-edit-${v.id}`}>Editar</Button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <code className="text-[11px] text-slate-400 bg-[#0B2545] px-2 py-1 rounded flex-1 truncate" data-testid={`drawer-qr-token-${v.id}`}>{v.qr_token}</code>
                        <button onClick={() => { navigator.clipboard.writeText(v.qr_token); toast.success("Token copiado"); }}
                          className="text-slate-400 hover:text-white" data-testid={`drawer-qr-copy-${v.id}`}><Copy className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setRegenVehicle(v)}
                          className="text-red-400/80 hover:text-red-300 text-[11px] underline" data-testid={`drawer-qr-regen-${v.id}`}>regenerar QR</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {data.vehicles.length === 0 && <p className="text-slate-500 text-sm">Sin vehículos</p>}
            </Section>

            {/* c) Push */}
            <Section icon={Bell} title={`Salud de push (${data.push_devices.length} dispositivo/s)`} testId="drawer-push-section">
              {data.push_devices.map((d, i) => (
                <div key={i} className="flex justify-between items-center text-xs py-1.5 border-b border-white/10/60 last:border-0">
                  <div>
                    <span className={d.enabled ? "text-emerald-300" : "text-red-400"}>{d.enabled ? "● activo" : "● inactivo"}</span>
                    <span className="text-slate-400 ml-2">{d.platform || "?"}</span>
                    {d.failure_reason && <span className="text-red-400/70 ml-2">{d.failure_reason}</span>}
                  </div>
                  <div className="text-slate-500">
                    último registro {fmt(d.last_seen_at, "d MMM HH:mm")} · vivo {d.last_alive_at ? fmt(d.last_alive_at, "d MMM HH:mm") : "—"} · http {d.http_status ?? "—"}
                  </div>
                </div>
              ))}
              {data.push_devices.length === 0 && (
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-xs text-slate-300"
                     data-testid="drawer-push-no-device">
                  <p className="font-semibold text-slate-200">Sin dispositivo registrado.</p>
                  <p className="mt-1">
                    No hay a dónde enviar un aviso: no es un fallo de entrega. El cliente debe abrir
                    su panel en el móvil, instalar AvisoCar en la pantalla de inicio y aceptar los
                    permisos de notificación. Hasta entonces, un push de prueba no puede llegar.
                  </p>
                  <p className="mt-1 text-slate-400">
                    Con la app cerrada en Android/iOS hace falta FCM/APNs, que hoy está bloqueado
                    por credenciales del proveedor.
                  </p>
                </div>
              )}
              <Button size="sm" onClick={testPush}
                disabled={busy || (data.push_devices.length === 0)}
                title={data.push_devices.length === 0
                  ? "No se puede enviar: este cliente no tiene ningún dispositivo registrado"
                  : "Enviar un aviso de prueba a sus dispositivos"}
                className="mt-3 bg-[#1B84FF] hover:bg-[#1B84FF] h-8 text-xs disabled:cursor-not-allowed disabled:opacity-35"
                data-testid="drawer-test-push-btn">
                <Bell className="w-3.5 h-3.5 mr-1" /> Enviar aviso de prueba
              </Button>
              <Button size="sm" onClick={reviveUser} disabled={busy}
                className="mt-3 bg-red-600 hover:bg-red-500 h-8 text-xs ml-2" data-testid="drawer-revive-btn">
                <HeartPulse className="w-3.5 h-3.5 mr-1" /> 🫀 Revivir avisos
              </Button>
              <Button size="sm" variant="outline" onClick={downloadForensics}
                className="border-white/15 text-slate-300 h-8 text-xs ml-2" data-testid="drawer-forensics-btn">
                Forense push (72h)
              </Button>
              {/* iter68 F4 — guía en vez de error rojo cuando no hay dispositivos */}
              {pushGuideHint && (
                <div className="mt-2 p-3 rounded-lg bg-sky-500/10 border border-sky-400/30" data-testid="drawer-push-guide-hint">
                  <p className="text-sky-200 text-xs mb-2">
                    Este usuario no tiene dispositivos push registrados. Reenvíale la guía de instalación.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => sendGuide("copy")}
                      className="bg-sky-500 hover:bg-sky-400 text-black h-7 text-xs font-bold" data-testid="drawer-push-hint-guide-btn">
                      <Copy className="w-3 h-3 mr-1" /> Reenviar guía
                    </Button>
                    {data.profile.phone && (
                      <Button size="sm" onClick={() => sendGuide("wa")}
                        className="bg-[#1B84FF] hover:bg-[#1B84FF] h-7 text-xs">
                        Por WhatsApp
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </Section>

            {/* e) Actividad — SOLO METADATOS */}
            <Section icon={Activity} title="Actividad (metadatos — el contenido de mensajes es privado)" testId="drawer-activity-section">
              <div className="grid grid-cols-3 gap-2 text-center text-sm mb-2">
                <div className="bg-black/30 rounded p-2"><p className="text-white font-bold">{data.activity.total_sessions}</p><p className="text-slate-500 text-[11px]">sesiones privadas</p></div>
                <div className="bg-black/30 rounded p-2"><p className="text-white font-bold">{data.activity.sessions_by_type?.call || 0}</p><p className="text-slate-500 text-[11px]">llamadas</p></div>
                <div className="bg-black/30 rounded p-2"><p className="text-white font-bold">{data.activity.blocked_sessions}</p><p className="text-slate-500 text-[11px]">bloqueos del dueño</p></div>
              </div>
              {data.activity.recent_sessions?.map((s, i) => (
                <p key={i} className="text-[11px] text-slate-500">· {s.type || "chat"} — {s.status} — {fmt(s.created_at)}</p>
              ))}
            </Section>

            {/* f) Pagos + vouchers */}
            <Section icon={CreditCard} title="Pagos y vouchers" testId="drawer-payments-section">
              <label className="inline-flex items-center gap-2 text-xs text-slate-300 bg-[#14375F] hover:bg-slate-700 rounded-lg px-3 py-2 cursor-pointer mb-3">
                <Upload className="w-3.5 h-3.5" /> Subir voucher (imagen/PDF)
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
                  onChange={(e) => uploadVoucher(e.target.files?.[0])} data-testid="drawer-voucher-input" />
              </label>
              {data.payments.map((p) => (
                <div key={p.id} className="flex justify-between items-center text-xs py-1.5 border-b border-white/10/60 last:border-0" data-testid={`drawer-payment-${p.id}`}>
                  <div>
                    <span className="text-white">S/{p.amount}</span>
                    <span className={`ml-2 ${p.status === "approved" ? "text-emerald-300" : p.status === "rejected" ? "text-red-400" : "text-amber-300"}`}>{p.status}</span>
                    {p.payment_ref && <span className="text-slate-400 ml-2">ref: {p.payment_ref}</span>}
                    <span className="text-slate-500 ml-2">{fmt(p.created_at, "d MMM yyyy")}</span>
                    {p.has_voucher && (
                      <button
                        onClick={async () => {
                          try {
                            const r = await axios.get(`${API}/admin/payments/${p.id}/voucher`, { responseType: "blob" });
                            window.open(URL.createObjectURL(r.data), "_blank");
                          } catch (_) { toast.error("No se pudo abrir el voucher"); }
                        }}
                        className="text-[#5BC0FF] underline ml-2"
                      >voucher</button>
                    )}
                  </div>
                  {p.status === "pending" && (
                    <div className="flex gap-1">
                      <button onClick={() => reviewPayment(p.id, "approved")} className="text-[#5BC0FF] hover:text-emerald-300 underline" data-testid={`drawer-pay-approve-${p.id}`}>aprobar</button>
                      <button onClick={() => reviewPayment(p.id, "rejected")} className="text-red-400 hover:text-red-300 underline ml-2" data-testid={`drawer-pay-reject-${p.id}`}>rechazar</button>
                    </div>
                  )}
                </div>
              ))}
              {data.payments.length === 0 && <p className="text-slate-500 text-sm">Sin pagos registrados</p>}
            </Section>

            {/* 1.3 Notas internas */}
            <Section icon={StickyNote} title="Notas internas (solo admin)" testId="drawer-notes-section">
              <div className="flex gap-2 mb-3">
                <Input value={noteText} onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addNote()}
                  placeholder="Escribe una nota interna..." className="bg-[#0B2545] border-white/15 text-white h-9" data-testid="drawer-note-input" />
                <Button size="sm" onClick={addNote} className="bg-slate-700 hover:bg-slate-600 h-9" data-testid="drawer-note-add">Añadir</Button>
              </div>
              {data.notes.map((n) => (
                <p key={n.id} className="text-xs text-slate-300 py-1 border-b border-white/10/60 last:border-0">
                  {n.text} <span className="text-slate-600">— {fmt(n.ts)}</span>
                </p>
              ))}
            </Section>
          </>
        )}

        {/* Sub modal (extender días / regalar meses) */}
        {subModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={() => setSubModal(null)} />
            <div className="relative bg-[#0B2545] border border-white/15 rounded-xl p-5 w-full max-w-sm" data-testid="sub-action-modal">
              <h4 className="text-white font-semibold mb-3">{subModal.title}</h4>
              <Input type="number" min="1" value={subModalValue} onChange={(e) => setSubModalValue(e.target.value)}
                placeholder={`Cantidad de ${subModal.unit}`} className="bg-[#071B2F] border-white/15 text-white mb-2" data-testid="sub-modal-amount" />
              <Input value={subModalReason} onChange={(e) => setSubModalReason(e.target.value)}
                placeholder={subModal.needsReason ? "Motivo (obligatorio)" : "Motivo (opcional)"}
                className="bg-[#071B2F] border-white/15 text-white mb-3" data-testid="sub-modal-reason" />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setSubModal(null)} className="text-slate-400">Cancelar</Button>
                <Button size="sm" onClick={confirmSubModal} disabled={busy} className="bg-[#1B84FF] hover:bg-[#1B84FF]" data-testid="sub-modal-confirm">Confirmar</Button>
              </div>
            </div>
          </div>
        )}

        {/* Regenerate QR — double confirmation */}
        {regenVehicle && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={() => setRegenVehicle(null)} />
            <div className="relative bg-[#0B2545] border border-red-800 rounded-xl p-5 w-full max-w-sm" data-testid="regen-qr-modal">
              <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
                <AlertTriangle className="w-5 h-5" /> Regenerar QR — irreversible
              </div>
              <p className="text-slate-300 text-sm mb-3">
                Se emitirá un token NUEVO: <strong>el sticker impreso actual dejará de funcionar para siempre</strong>. El dueño deberá imprimir un sticker nuevo.
              </p>
              <p className="text-slate-400 text-xs mb-2">Escribe <strong className="text-white">REGENERAR</strong> para confirmar:</p>
              <Input value={regenConfirmText} onChange={(e) => setRegenConfirmText(e.target.value)}
                className="bg-[#071B2F] border-white/15 text-white mb-3" data-testid="regen-confirm-input" />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => { setRegenVehicle(null); setRegenConfirmText(""); }} className="text-slate-400">Cancelar</Button>
                <Button size="sm" onClick={regenerateQR} disabled={busy || regenConfirmText !== "REGENERAR"}
                  className="bg-red-700 hover:bg-red-600" data-testid="regen-confirm-btn">Regenerar QR</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
